import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, Separator } from '@scio3/ui';
import { GET_INVOICES_UNPAID } from '@scio3/graphql-client';
import { safeFormatDate } from '@/lib/safe-format';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { formatCurrencyAmount } from '../utils/plan-helpers';
import { useBillingData } from '../hooks/useBillingData';

type UnpaidInvoice = {
  id: string;
  number?: string;
  amount: number;
  created: number;
  status: string;
};

/**
 * Blocking dialog shown when user tries to open the subscription wizard
 * but has outstanding unpaid invoices. Matching scio1's pre-check behavior.
 * Displays invoice table + "Update Payment Method" button.
 */
export function UnpaidInvoicesDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);
  const isOpen = activeDialog === 'unpaidInvoices';
  const contextSubAccountId = dialogContext.subAccountId as string | undefined;
  const contextTeamId = dialogContext.teamId as string | undefined;

  const { currency } = useBillingData({
    subAccountId: contextSubAccountId,
    teamId: contextTeamId,
    skip: !isOpen,
  });

  const { data, loading } = useQuery<{
    getInvoicesUnpaid: { status: boolean; invoices: UnpaidInvoice[] };
  }>(GET_INVOICES_UNPAID, {
    variables: { subAccountId: contextSubAccountId, teamId: contextTeamId },
    skip: !isOpen,
    fetchPolicy: 'network-only',
  });

  const invoices = (data?.getInvoicesUnpaid?.invoices ?? []) as UnpaidInvoice[];
  const total = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  const handleUpdatePayment = useCallback(() => {
    closeDialog();
    // Navigate to payment settings page
    window.location.href = '/app/s/payment';
  }, [closeDialog]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeDialog();
    },
    [closeDialog],
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-lg"
        data-testid="unpaid-invoices-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            {t('You have outstanding invoices')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <p className="text-sm text-muted-foreground mb-4">
            {t('Please resolve your outstanding invoices before making subscription changes.')}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-2 text-left font-medium">{t('Invoice ID')}</th>
                  <th className="py-2 text-left font-medium">{t('Status')}</th>
                  <th className="py-2 text-left font-medium">{t('Date')}</th>
                  <th className="py-2 text-right font-medium">{t('Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b">
                    <td className="py-2">{inv.number ?? inv.id}</td>
                    <td className="py-2 capitalize">{t(inv.status)}</td>
                    <td className="py-2">
                      {safeFormatDate(new Date(inv.created * 1000), 'MM/dd/yyyy')}
                    </td>
                    <td className="py-2 text-right">
                      {formatCurrencyAmount(inv.amount / 100, currency)}
                    </td>
                  </tr>
                ))}
                <tr className="font-medium">
                  <td className="py-2" colSpan={3}>
                    {t('Total amount due')}
                  </td>
                  <td className="py-2 text-right">{formatCurrencyAmount(total / 100, currency)}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('No outstanding invoices found.')}
            </p>
          )}

          <Separator className="my-4" />

          <div className="flex justify-center">
            <Button onClick={handleUpdatePayment} className="cursor-pointer">
              {t('Update Payment Method')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
